/**
 * Environment Configuration
 *
 * Provides type-safe access to environment variables with Zod validation.
 * Fails fast on startup if required environment variables are missing or invalid.
 *
 * @module config/env
 */

import { z } from 'zod';

const optionalUrl = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
  z.string().url().optional()
);

const booleanFromEnv = z.preprocess((value) => {
  if (value === undefined) return undefined;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === '') return undefined;

    if (['true', '1', 'yes', 'y', 'on'].includes(normalized)) return true;
    if (['false', '0', 'no', 'n', 'off'].includes(normalized)) return false;
  }

  return value;
}, z.boolean());

/**
 * Schema for environment variables.
 * Each variable has explicit typing and optional default values.
 */
const envSchema = z.object({
  /**
   * Milestone Service URL.
   * Used for triggering milestone evaluations and automation workflows.
   */
  VITE_MILESTONE_SERVICE_URL: z
    .string()
    .url()
    .default('https://coordi-milestone-service-staging.up.railway.app'),

  /**
   * Railway AI service URL.
   */
  VITE_RAILWAY_API_URL: optionalUrl,

  /**
   * Runtime environment labels.
   */
  VITE_ENVIRONMENT: z.string().optional(),
  VITE_APP_ENV: z.string().optional(),

  /**
   * Supabase URL for database connections.
   */
  VITE_SUPABASE_URL: optionalUrl,

  /**
   * Supabase publishable key (anon key).
   */
  VITE_SUPABASE_PUBLISHABLE_KEY: z.string().optional(),

  /**
   * Legacy Supabase anon key (fallback).
   */
  VITE_SUPABASE_ANON_KEY: z.string().optional(),

  /**
   * Sentry DSN for frontend error monitoring.
   */
  VITE_SENTRY_DSN: optionalUrl,

  /**
   * Sentry environment label.
   */
  VITE_SENTRY_ENVIRONMENT: z.string().optional(),

  /**
   * Sentry trace sampling rate.
   */
  VITE_SENTRY_TRACES_SAMPLE_RATE: z.coerce.number().min(0).max(1).optional(),

  /**
   * Session replay sample rate.
   */
  VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE: z.coerce.number().min(0).max(1).optional(),

  /**
   * Replay-on-error sample rate.
   */
  VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE: z.coerce.number().min(0).max(1).optional(),
  VITE_LOG_SENSITIVE_PAYLOADS: booleanFromEnv.default(false),

  /**
   * Enables SMS setup and automation-editing surfaces in the portal.
   * Leave unset to enable outside production and disable in production.
   */
  VITE_SMS_UI_ENABLED: booleanFromEnv.optional(),

  /**
   * PostHog project API key.
   */
  VITE_POSTHOG_KEY: z.string().optional(),

  /**
   * PostHog API host.
   */
  VITE_POSTHOG_HOST: z.string().optional().default('https://us.i.posthog.com'),
}).refine(
  (data) => {
    if (data.VITE_SUPABASE_URL) {
      return data.VITE_SUPABASE_PUBLISHABLE_KEY || data.VITE_SUPABASE_ANON_KEY;
    }
    return true;
  },
  { message: 'VITE_SUPABASE_URL requires at least one auth key (VITE_SUPABASE_PUBLISHABLE_KEY or VITE_SUPABASE_ANON_KEY)' }
);

/**
 * Parsed and validated environment configuration.
 *
 * @description Type-safe environment configuration object inferred from Zod schema.
 * Guarantees that all required variables are present and valid according to the schema constraints.
 * Supabase variables, when present, maintain mutual dependency validation:
 * if VITE_SUPABASE_URL is provided, at least one authentication key must be present.
 */
export type EnvConfig = z.infer<typeof envSchema>;

/**
 * Validates and returns the environment configuration.
 *
 * @description Parses environment variables from import.meta.env with Zod validation.
 * Falls back to default values where specified in the schema.
 *
 * @returns Validated environment configuration object
 * @throws {z.ZodError} If required environment variables are missing or invalid
 *
 * @example
 * ```typescript
 * const config = getEnvConfig();
 * console.log(config.VITE_MILESTONE_SERVICE_URL);
 * // => "https://coordi-milestone-service-staging.up.railway.app"
 * ```
 */
export function getEnvConfig(): EnvConfig {
  // In browser context, use import.meta.env
  // In test context, this may be undefined, so we provide an empty object
  const rawEnv = typeof import.meta !== 'undefined' && import.meta.env
    ? import.meta.env
    : {};

  const result = envSchema.safeParse(rawEnv);

  if (!result.success) {
    // Log validation errors but don't throw - use defaults
    // This allows the app to run with default values in development
    if (typeof window !== 'undefined' && import.meta.env?.DEV) {
      console.warn('[env] Environment validation warnings:', result.error.issues);
    }

    // Return defaults by parsing an empty object
    return envSchema.parse({});
  }

  return result.data;
}

/**
 * Cached environment configuration instance.
 *
 * @description Stores the parsed and validated environment configuration to avoid
 * repeated Zod validation overhead. Set to null initially and populated on first call
 * to getEnv(). Subsequent calls return the cached instance without re-parsing.
 * This ensures consistent configuration throughout the application lifecycle.
 */
let cachedConfig: EnvConfig | null = null;

/**
 * Gets the cached environment configuration.
 *
 * @description Returns a cached instance of the environment configuration
 * to avoid repeated parsing overhead. Safe to call multiple times.
 *
 * @returns Validated environment configuration object
 * @throws {never} This function does not throw
 *
 * @example
 * ```typescript
 * import { env } from '@/config/env';
 *
 * fetch(`${env.VITE_MILESTONE_SERVICE_URL}/api/evaluate`, { ... });
 * ```
 */
export function getEnv(): EnvConfig {
  if (!cachedConfig) {
    cachedConfig = getEnvConfig();
  }
  return cachedConfig;
}

/**
 * Pre-configured environment instance for direct import.
 * Lazily initialized on first access.
 *
 * @description Uses a Proxy to provide lazy evaluation and caching of environment
 * configuration. This approach defers parsing and validation until the first property
 * access rather than module initialization time. Each property access triggers getEnv(),
 * which returns the cached configuration, avoiding re-parsing while ensuring fresh
 * data on module reload during development.
 */
export const env = new Proxy({} as EnvConfig, {
  get(_target, prop: string | symbol) {
    if (typeof prop === 'symbol') return undefined;
    const config = getEnv();
    if (prop in config) {
      return config[prop as keyof EnvConfig];
    }
    return undefined;
  },
});
